import React, { useState, useEffect, useCallback, useRef } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronLeft,
  faChevronRight,
} from "@fortawesome/free-solid-svg-icons";
import { fetchJson } from "../utils/fetchJson";

export default function Carousel() {
  const [slides, setSlides] = useState([]);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isPlaying, setIsPlaying] = useState(true);

  const intervalRef = useRef(null);
  const touchStartRef = useRef(null);
  const touchEndRef = useRef(null);

  // 获取
  useEffect(() => {
    async function load() {
      try {
        const res = await fetchJson("/json/banner.json");
        if (!res.ok) throw new Error("Failed to load carousel data");
        const data = await res.json();
        if (!Array.isArray(data) || data.length === 0) {
          setSlides([]);
        } else {
          setSlides(data);
          const activeIndex = data.findIndex((s) => s.active);
          setCurrentSlide(activeIndex >= 0 ? activeIndex : 0);
        }
        setIsLoading(false);
      } catch (err) {
        setError(err.message || "Unknown error");
        setIsLoading(false);
      }
    }
    load();
  }, []);

  const clampIndex = useCallback(
    (index) => {
      if (!slides || slides.length === 0) return 0;
      const n = slides.length;
      return ((index % n) + n) % n;
    },
    [slides]
  );

  const goToSlide = useCallback(
    (index, { resetTimer = true } = {}) => {
      setCurrentSlide((_) => clampIndex(index));
      if (resetTimer) restartInterval();
    },
    [clampIndex]
  );

  const nextSlide = useCallback(() => {
    setCurrentSlide((prev) => clampIndex(prev + 1));
    restartInterval();
  }, [clampIndex]);

  const prevSlide = useCallback(() => {
    setCurrentSlide((prev) => clampIndex(prev - 1));
    restartInterval();
  }, [clampIndex]);

  const clearExistingInterval = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  };

  const restartInterval = () => {
    clearExistingInterval();
    if (!isPlaying || !slides || slides.length <= 1) return;
    intervalRef.current = setInterval(() => {
      setCurrentSlide((prev) => clampIndex(prev + 1));
    }, 5000);
  };

  useEffect(() => {
    clearExistingInterval();
    if (isPlaying && slides.length > 1) {
      intervalRef.current = setInterval(() => {
        setCurrentSlide((prev) => clampIndex(prev + 1));
      }, 5000);
    }
    return () => clearExistingInterval();
  }, [isPlaying, slides.length]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "ArrowLeft") {
        prevSlide();
      } else if (e.key === "ArrowRight") {
        nextSlide();
      } else if (e.code === "Space") {
        e.preventDefault();
        setIsPlaying((p) => !p);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [nextSlide, prevSlide]);

  const handleTouchStart = (e) => {
    touchEndRef.current = null;
    touchStartRef.current = e.targetTouches[0].clientX;
  };
  const handleTouchMove = (e) => {
    touchEndRef.current = e.targetTouches[0].clientX;
  };
  const handleTouchEnd = () => {
    const start = touchStartRef.current;
    const end = touchEndRef.current;
    if (start == null || end == null) return;
    const diff = start - end;
    if (Math.abs(diff) < 40) return;
    if (diff > 0) {
      nextSlide();
    } else {
      prevSlide();
    }
  };

  const handleMouseEnter = () => {
    setIsPlaying(false);
  };
  const handleMouseLeave = () => {
    setIsPlaying(true);
  };

  useEffect(() => {
    restartInterval();
    return () => clearExistingInterval();
  }, [slides.length]);

  if (isLoading) {
    return (
      <div className="carousel-container" style={{ minHeight: "200px" }}>
        <div style={{ textAlign: "center", padding: 20 }}>
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading carousel...</span>
          </div>
          <p className="mt-2 text-muted">Loading latest updates...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="carousel-container" style={{ minHeight: "200px" }}>
        <div style={{ padding: 20 }}>
          <div className="alert alert-warning" role="alert">
            <h4 className="alert-heading">Unable to load carousel</h4>
            <p className="mb-0">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  if (!slides || slides.length === 0) {
    return null;
  }

  const styleBlock = `
  .carousel-root { position: relative; width: 100%;aspect-ratio: 16 / 9; max-height: 400px; overflow: hidden; border-radius: 12px; box-shadow: 0 10px 30px rgba(0,0,0,0.25); }
  .carousel-slide { 
    position: absolute; 
    inset: 0; 
    transition: opacity 700ms ease, transform 700ms ease; 
    opacity: 0; 
    transform: scale(1.02); 
    display: flex;
  }
  
  .carousel-slide.show { 
    opacity: 1; 
    transform: scale(1); 
    z-index: 2; 
  }
  
  .carousel-image-box {
    width: 100%;
    height: 100%;
    padding:20px;
    background: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  
  .carousel-image-box img {
    max-width: 100%;
    min-height: 100%;
    object-fit: contain;
    border-radius: 12px;
  }
  
  .carousel-slide.show { opacity: 1; transform: scale(1); z-index: 2; }
  .carousel-overlay { position: absolute; inset: 0; display:flex; align-items:flex-end; justify-content:center; z-index:3; pointer-events:none; background-image: linear-gradient(180deg, rgba(0,0,0,0.05) 30%, rgba(0,0,0,0.6) 100%); }
  .carousel-content { width:100%; box-sizing: border-box; pointer-events:auto; text-align:center; color:#fff;}
  .carousel-title { font-weight:700; font-size:1.15rem; text-decoration:none; color:#fff; display:block; max-width:90%; margin: 0 auto; white-space:nowrap; overflow:hidden; text-overflow:ellipsis;}
  .carousel-title:hover{color:"#fff"}
  .carousel-btn { pointer-events:auto;}
  .carousel-indicators { display:flex; gap:8px; z-index:4; pointer-events:auto;position:static }
  .carousel-dot { width:10px; height:10px; border-radius:50%; border:2px solid rgba(255,255,255,0.7); background: transparent; padding:0; cursor:pointer; }
  .carousel-dot.active { background: rgba(255,255,255,0.95); border-color: rgba(255,255,255,0.95); }
  .carousel-nav { position:absolute; top:50%; transform:translateY(-50%); width:44px; height:44px; border-radius:50%; border:none; display:flex; align-items:center; justify-content:center; cursor:pointer; z-index:5; background: rgba(255,255,255,0.12); color:#fff; }
  .carousel-nav:hover { background: rgba(255,255,255,0.22); }
  .carousel-nav.left { left:14px; }
  .carousel-nav.right { right:14px; }
  `;

  const current = slides[currentSlide];

  return (
    <div
      className="carousel-root"
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      style={{ position: "relative" }}
    >
      <style>{styleBlock}</style>

      {/* slides (cross-fade) */}
      {slides.map((s, idx) => {
        const isActive = idx === currentSlide;
        return (
          <div
            key={s.id ?? idx}
            className={`carousel-slide ${isActive ? "show" : ""}`}
          >
            <div className="carousel-image-box">
              <img src={s.image} alt={s.title} />
            </div>
          </div>
        );
      })}

      {/* overlay content */}
      <div className="carousel-overlay" aria-hidden={false}>
        <div className="carousel-content">
          {/* --- 标题链接 --- */}
          {current.link ? (
            <a
              href={current.link}
              className="carousel-title"
              target={current.link.startsWith("http") ? "_blank" : "_self"}
              rel="noopener noreferrer"
              title={current.title}
            >
              {current.title}
            </a>
          ) : (
            <span
              className="carousel-title"
              title={current.title}
              style={{ cursor: "default" }}
            >
              {current.title}
            </span>
          )}

          {/* --- 按钮区 --- */}
          <div className="carousel-btn" style={{ margin: "10px 0" }}>
            {current.link ? (
              <a
                href={current.link}
                className="btn btn-primary"
                target={current.link.startsWith("http") ? "_blank" : "_self"}
                rel="noopener noreferrer"
                style={{
                  padding: "10px 22px",
                  borderRadius: 24,
                  fontWeight: 600,
                  textDecoration: "none",
                }}
              >
                {current.button_text || "Learn More"}
              </a>
            ) : (
              <button
                className="btn btn-secondary"
                style={{
                  padding: "10px 22px",
                  borderRadius: 24,
                  display: "none",
                  fontWeight: 600,
                  opacity: 0.6,
                  cursor: "not-allowed",
                }}
              >
                {current.button_text || "Learn More"}
              </button>
            )}
          </div>

          {slides.length > 1 && (
            <div
              className="carousel-indicators"
              role="tablist"
              aria-label="Slides"
              style={{ marginBottom: "5px" }}
            >
              {slides.map((s, i) => (
                <button
                  key={s.id ?? i}
                  className={`carousel-dot ${
                    i === currentSlide ? "active" : ""
                  }`}
                  onClick={() => goToSlide(i)}
                  aria-label={`Go to slide ${i + 1}: ${s.title}`}
                  title={s.title}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {slides.length > 1 && (
        <>
          <button
            className="carousel-nav left"
            aria-label="Previous slide"
            onClick={(e) => {
              e.stopPropagation();
              prevSlide();
            }}
            title="Previous"
          >
            <FontAwesomeIcon icon={faChevronLeft} />
          </button>

          <button
            className="carousel-nav right"
            aria-label="Next slide"
            onClick={(e) => {
              e.stopPropagation();
              nextSlide();
            }}
            title="Next"
          >
            <FontAwesomeIcon icon={faChevronRight} />
          </button>
        </>
      )}
    </div>
  );
}
